<template>
  <NuxtLayout name="home">
    <div class="mx-auto flex min-h-screen w-full max-w-6xl flex-col items-center justify-center px-[18px] pb-20 pt-28 text-center sm:px-7">
      <p class="mb-3 font-mono text-xs font-medium uppercase tracking-[0.12em] text-subtle-foreground">{{ error.statusCode }}</p>
      <h1 class="mb-3 max-w-[20ch] text-balance text-[clamp(26px,4vw,44px)] font-bold leading-[1.08] tracking-[-0.035em]">{{ title }}</h1>
      <p class="mb-8 max-w-[54ch] text-pretty text-base text-muted-foreground">{{ description }}</p>

      <!-- 404: the same working box as the landing, so a dead link still ends in a result -->
      <template v-if="isNotFound">
        <form class="w-full max-w-[640px]" novalidate @submit.prevent="submit">
          <UrlBox class="p-2 ps-4" :class="urlError ? 'border-destructive focus-within:border-destructive focus-within:ring-destructive/25' : ''">
            <input
              ref="inputRef"
              v-model="url"
              type="text"
              inputmode="url"
              autocomplete="off"
              class="min-w-0 flex-1 border-none bg-transparent text-base outline-none placeholder:text-subtle-foreground"
              :placeholder="t('landing.seo.cta.placeholder')"
              :aria-label="t('landing.seo.hero.urlLabel')"
              :aria-invalid="urlError ? 'true' : undefined"
              :aria-describedby="urlError ? 'error-url-error' : undefined"
              @input="urlError = ''"
            />
            <PasteClearButton v-model="url" @pasted="focusInput" @cleared="focusInput" />
            <template #action>
              <Button type="submit" class="flex-none">{{ t("landing.seo.hero.submit") }}</Button>
            </template>
          </UrlBox>
          <p v-if="urlError" id="error-url-error" role="alert" class="mt-2.5 text-start text-sm text-destructive">{{ urlError }}</p>
        </form>
        <nav class="mt-6 flex flex-wrap items-center justify-center gap-2" :aria-label="t('common.navTools')">
          <NuxtLink v-for="link in toolLinks" :key="link.to" :to="link.to" :class="buttonVariants({ variant: 'soft', size: 'sm' })">{{ link.label }}</NuxtLink>
          <NuxtLink to="/" :class="buttonVariants({ variant: 'ghost', size: 'sm' })">{{ t("errors.page.backHome") }}</NuxtLink>
        </nav>
      </template>

      <Button v-else @click="clearError({ redirect: '/' })">{{ t("errors.page.backHome") }}</Button>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { NuxtError } from "#app";
import { buttonVariants } from "@/components/ui/button";
import { coercePastedLink } from "~/utils/media-url-platforms";

const props = defineProps<{ error: NuxtError }>();
const { t } = useI18n();

const isNotFound = computed(() => props.error.statusCode === 404);
const title = computed(() => (isNotFound.value ? t("errors.page.notFoundTitle") : t("errors.page.genericTitle")));
const description = computed(() =>
  isNotFound.value ? t("errors.page.notFoundDescription") : t("errors.page.genericDescription"),
);

useHead({
  title,
  meta: [{ name: "robots", content: "noindex, nofollow" }],
});

const toolLinks = computed(() => [
  { to: "/downloader", label: t("pages.downloader.title") },
  { to: "/video-to-subtitle", label: t("pages.audioToSubtitle.title") },
  { to: "/subtitle-translator", label: t("pages.subtitle_translator.title") },
]);

const url = ref("");
const urlError = ref("");
const inputRef = ref<HTMLInputElement | null>(null);
const focusInput = () => inputRef.value?.focus();

// clearError resets Nuxt's error state first, otherwise the target route
// would still render this page. The query shape matches the landing form
// (SubtitleDownloader decodes `url` once more on arrival).
const submit = async () => {
  const link = coercePastedLink(url.value);
  if (!link) {
    urlError.value = t("landing.seo.hero.invalidUrl");
    return;
  }
  clearError();
  await navigateTo({ path: "/downloader", query: { url: encodeURIComponent(link) } });
};
</script>
